import { clipBbox2, type Bbox2 } from "../geo/bbox";
import { isAbort } from "../util/async";
import type { StackLayer } from "./OrthoStack";
import { rasterToImageData } from "./rasterImage";

export interface RenderTarget {
  ctx: CanvasRenderingContext2D;
  pixelWidth: number;
  pixelHeight: number;
  /** World (CRS) extent the target's full pixel rectangle covers. */
  extent: Bbox2;
}

/**
 * Paints every visible layer bottom-to-top onto `target`, each read at whatever overview
 * level matches the pixels it will actually occupy. Layers are painted with normal alpha
 * compositing (`drawImage`'s default), so a layer with nodata/alpha edges lets whatever is
 * beneath it show through — and an opaque layer simply covers what's beneath it, which is
 * the whole of "picking which raster wins in an overlap": stack order decides it.
 *
 * Used both for the interactive on-screen preview and, at a different resolution/extent, for
 * PDF export — one code path for what ends up on screen and what ends up on paper.
 */
export async function compositeStack(layers: StackLayer[], target: RenderTarget, signal?: AbortSignal): Promise<void> {
  const { ctx, pixelWidth, pixelHeight, extent } = target;
  const worldToPx = (x: number, y: number) => ({
    x: ((x - extent.minX) / (extent.maxX - extent.minX)) * pixelWidth,
    y: ((extent.maxY - y) / (extent.maxY - extent.minY)) * pixelHeight,
  });

  for (const layer of layers) {
    if (signal?.aborted) return;
    const clip = clipBbox2(layer.raster.bbox, extent);
    if (!clip) continue;

    const topLeft = worldToPx(clip.minX, clip.maxY);
    const bottomRight = worldToPx(clip.maxX, clip.minY);
    const destX = Math.round(topLeft.x);
    const destY = Math.round(topLeft.y);
    const destW = Math.round(bottomRight.x) - destX;
    const destH = Math.round(bottomRight.y) - destY;
    if (destW <= 0 || destH <= 0) continue;

    try {
      const worldPerPxX = (clip.maxX - clip.minX) / destW;
      const level = layer.raster.levelForResolution(worldPerPxX);
      const topLeftPx = layer.raster.crsToPixel(level, clip.minX, clip.maxY);
      const bottomRightPx = layer.raster.crsToPixel(level, clip.maxX, clip.minY);
      const window: [number, number, number, number] = [
        Math.floor(topLeftPx.px),
        Math.floor(topLeftPx.py),
        Math.ceil(bottomRightPx.px),
        Math.ceil(bottomRightPx.py),
      ];
      const raw = await layer.raster.readWindow(level, window, { outWidth: destW, outHeight: destH, signal });
      if (signal?.aborted || raw.width === 0 || raw.height === 0) continue;

      const imageData = rasterToImageData(raw, layer.raster.hasAlpha);
      const bitmap = await createImageBitmap(imageData);
      if (signal?.aborted) {
        bitmap.close();
        continue;
      }
      ctx.drawImage(bitmap, destX, destY, destW, destH);
      bitmap.close();
    } catch (error) {
      if (!isAbort(error)) console.warn(`[plan] layer ${layer.source.label} failed to render:`, error);
    }
  }
}
